import { useState } from 'react';
import { formatCount, formatDuration, formatNumber } from '../../engine';
import { gameStore } from '../../game/store';
import type { Snapshot } from '../../game/store';

interface SettingsPanelProps {
  snapshot: Snapshot;
}

type Message = { tone: 'good' | 'bad'; text: string } | null;

export function SettingsPanel({ snapshot }: SettingsPanelProps) {
  const [saveText, setSaveText] = useState('');
  const [message, setMessage] = useState<Message>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const { state } = snapshot;
  const notation = state.settings.notation;

  const handleExport = async (): Promise<void> => {
    const exported = gameStore.exportSave();
    setSaveText(exported);
    try {
      await navigator.clipboard.writeText(exported);
      setMessage({ tone: 'good', text: 'Save copied to the clipboard.' });
    } catch {
      setMessage({ tone: 'good', text: 'Save written below — copy it by hand.' });
    }
  };

  const handleImport = (): void => {
    if (gameStore.importSave(saveText)) {
      setMessage({ tone: 'good', text: 'Save loaded.' });
    } else {
      setMessage({ tone: 'bad', text: 'That is not a save this game can read.' });
    }
  };

  return (
    <section className="panel">
      <h2 className="panel-title">Settings</h2>

      <div className="setting-row">
        <div>
          <div className="setting-label">Number notation</div>
          <div className="setting-desc">How very large numbers are written.</div>
        </div>
        <div className="tabs" style={{ margin: 0 }}>
          {(['standard', 'scientific'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={`tab${notation === option ? ' active' : ''}`}
              onClick={() => gameStore.updateSettings({ notation: option })}
            >
              {option === 'standard' ? '1.2M' : '1.2e6'}
            </button>
          ))}
        </div>
      </div>

      <div className="setting-row">
        <div>
          <div className="setting-label">Show rates</div>
          <div className="setting-desc">Per-second figures under each resource.</div>
        </div>
        <button
          type="button"
          className={`tab${state.settings.showRates ? ' active' : ''}`}
          onClick={() => gameStore.updateSettings({ showRates: !state.settings.showRates })}
        >
          {state.settings.showRates ? 'On' : 'Off'}
        </button>
      </div>

      <div className="setting-row">
        <div>
          <div className="setting-label">Confirm before resetting</div>
          <div className="setting-desc">Ask before passing the fire or changing age.</div>
        </div>
        <button
          type="button"
          className={`tab${state.settings.confirmPrestige ? ' active' : ''}`}
          onClick={() =>
            gameStore.updateSettings({ confirmPrestige: !state.settings.confirmPrestige })
          }
        >
          {state.settings.confirmPrestige ? 'On' : 'Off'}
        </button>
      </div>

      <h3 className="ancestral-tier-label" style={{ marginTop: 18 }}>
        The long count
      </h3>
      <div className="card-effects">
        <span className="effect-chip">Played {formatDuration(state.stats.totalPlaySeconds)}</span>
        <span className="effect-chip">{state.stats.totalGenerations} generations</span>
        <span className="effect-chip">Best tribe {formatCount(state.stats.bestPopulation)}</span>
        <span className="effect-chip">
          Best knowledge {formatNumber(state.stats.bestGenerationAk, notation)}
        </span>
        <span className="effect-chip">{state.stats.totalManualGathers} hands lent</span>
      </div>

      <h3 className="ancestral-tier-label" style={{ marginTop: 18 }}>
        Save
      </h3>
      <p className="setting-desc">
        The game saves itself to this browser. Export a string to carry it elsewhere.
      </p>
      <textarea
        className="save-box"
        value={saveText}
        onChange={(event) => setSaveText(event.target.value)}
        placeholder="Paste a save here to load it, or export to fill this box."
        spellCheck={false}
      />
      {message ? (
        <p className={message.tone === 'good' ? 'card-flavor' : 'card-note'}>{message.text}</p>
      ) : null}

      <div className="card-actions" style={{ marginTop: 10 }}>
        <button type="button" className="btn" onClick={handleExport}>
          Export
        </button>
        <button type="button" className="btn" onClick={handleImport} disabled={!saveText.trim()}>
          Import
        </button>
        <button
          type="button"
          className="btn danger"
          onClick={() => setConfirmingReset(true)}
        >
          Erase everything
        </button>
      </div>

      {confirmingReset ? (
        <div className="modal-backdrop" onClick={() => setConfirmingReset(false)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <h2>Erase the whole line?</h2>
            <p>
              Every generation, every rite, every scrap of Ancestral Memory. This cannot be undone
              unless you exported a save first.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  gameStore.hardReset();
                  setConfirmingReset(false);
                  setMessage({ tone: 'good', text: 'The fire was let go. A new one begins.' });
                }}
              >
                Erase it
              </button>
              <button type="button" className="btn" onClick={() => setConfirmingReset(false)}>
                Keep it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
